/**
 * structure_masks — every settlement building's WALLS AT WALKING HEIGHT, on the
 * client (structure collision wave, 2026-09-23: "make sure the structures'
 * collision matches the MESH of the structure for vehicles, ships and players").
 *
 * The server already walks its versonas against these: `bake:footprints`
 * rasterises each prefab's collider mesh between 0.3 and 2.6 m above its seat
 * into a 1 m occupancy MASK (walls, plinths, pillars — a prefab's BOX is not its
 * footprint: the civ prefabs are whole district blocks with streets inside), and
 * lists every placement of every site. The same numbers are exported for the
 * client as `public/footprints/<fnv(seed)>.json` (the proxies' filename law), and
 * this module is the server's `keyMask` / `maskSolid` / `decodeSite` SOLID raster
 * verbatim — no walk / main / BFS layers, a hull needs only "is there a wall
 * here, and where is the nearest open cell".
 *
 * A hovercraft on the ground (`hullBlockedAt`, AtlasVehicleModule.hoverBlocked)
 * asks the mask FIRST where a site's grid covers it: a free cell is free even
 * inside a building's ladder band (the compound's internal streets the old
 * base→roof box sealed), a wall cell pushes the hull to the nearest open cell;
 * only a building the mask does not know (no ladder — a port terminal, a legacy
 * key) still blocks by its box. Off the ground, or off the grid, the band solids
 * (lib/structure_solids.ts) answer as before.
 *
 * Sites decode lazily on first query into a byte-bounded LRU. The file may not
 * exist yet (it is baked by another step): a 404 means no walls, never an error.
 *
 * Diag: `__structureMasks()`.
 */
import { proxyFileFor } from './settlement_proxy.js';
import { structureHitAt, structurePushOut } from './structure_solids.js';

export const MASK_FORMAT_VERSION = 1;
/** Numbers per placement: keyIdx, lx, lz, yaw, scale (MIRROR: server FOOTPRINT_STRIDE). */
export const MASK_STRIDE = 5;
/** How far past a site's plan radius its grid extends (MIRROR: server SITE_REACH_M). */
export const MASK_SITE_REACH_M = 80;
/** The occupancy grid's cell (MIRROR: server GRID_CELL_M — 1 m keeps a 2 m gate open). */
export const MASK_GRID_CELL_M = 1.0;
/** Decoded grids kept, by BYTES (1 B/cell: a hamlet ~0.3 MB, a city ~1 MB, a capital core ~1.5 MB). */
export const MASK_CACHE_BYTES = 48 * 1024 * 1024;
/** The top of the walking band the masks were cut from (MIRROR: bake-footprints BAND[1]) — a hull
 *  below this above the ground is judged by the mask, above it by the ladder bands. */
export const WALL_BAND_TOP_M = 2.6;
/** wallPushOut's default search reach (cells). */
export const WALL_PUSH_MAX_CELLS = 4;

export interface MaskKey { key: string; pivot: number[]; x0?: number; z0?: number; w?: number; h?: number; words?: number[]; box?: number[] }
export interface MaskFile { v: number; seed: string; cell: number; keys: MaskKey[]; sites: Record<number, number[]> }
export interface MaskSite {
  id: number; cx: number; cz: number; r: number;
  /** grid origin (world), cells per side, cell size (m) */
  ox: number; oz: number; n: number; cell: number;
  solid: Uint8Array;
  placements: number;
}

/** The footprints file's name for a seed — the SAME fnv1a-32 the proxies use (one seed, one pair of files). */
export function maskFileFor(seed: string): string {
  return proxyFileFor(seed);
}

/** Validate a decoded footprints file; returns the reason it is unusable, or null. */
export function maskFileProblem(f: unknown, seed: string): string | null {
  const p = f as Partial<MaskFile> | null;
  if (!p || typeof p !== 'object') return 'not an object';
  if (p.v !== MASK_FORMAT_VERSION) return `format v${String(p.v)} ≠ ${MASK_FORMAT_VERSION}`;
  if (p.seed !== seed) return 'baked for another seed';
  if (!Array.isArray(p.keys) || !p.sites || typeof p.sites !== 'object') return 'missing keys/sites';
  if (!(typeof p.cell === 'number' && p.cell > 0)) return 'no mask cell';
  for (const k of p.keys) if (!Array.isArray(k?.pivot) || k.pivot.length !== 3) return `key ${String(k?.key)} has no pivot`;
  for (const id of Object.keys(p.sites)) {
    const raw = p.sites[+id];
    if (!Array.isArray(raw) || raw.length < 3 || (raw.length - 3) % MASK_STRIDE !== 0) return `site ${id} malformed`;
  }
  return null;
}

// ── The server's decode, verbatim (structure_footprints.ts keyMask / maskSolid / decodeSite) ──

interface KeyMask { pcx: number; pcz: number; x0: number; z0: number; w: number; h: number; words: number[] | null; hw: number; hd: number }

function keyMaskOf(keys: MaskKey[], i: number, cache: Array<KeyMask | undefined>): KeyMask | null {
  const hit = cache[i];
  if (hit) return hit;
  const k = keys[i];
  if (!k) return null;
  const m: KeyMask = {
    pcx: k.pivot[0], pcz: k.pivot[2],
    x0: k.x0 ?? 0, z0: k.z0 ?? 0, w: k.w ?? 0, h: k.h ?? 0, words: k.words ?? null,
    hw: k.box ? k.box[0] / 2 : 0, hd: k.box ? k.box[1] / 2 : 0,
  };
  cache[i] = m;
  return m;
}

/** Is the natural-units prefab point (qx, qz) inside the prefab's walking-height mask? */
function maskSolid(m: KeyMask, qx: number, qz: number, maskCell: number): boolean {
  if (!m.words) return Math.abs(qx - m.pcx) <= m.hw && Math.abs(qz - m.pcz) <= m.hd;
  const gx = Math.floor((qx - m.x0) / maskCell), gz = Math.floor((qz - m.z0) / maskCell);
  if (gx < 0 || gz < 0 || gx >= m.w || gz >= m.h) return false;
  const bit = gz * m.w + gx;
  return ((m.words[(bit / 30) | 0] >>> (bit % 30)) & 1) === 1;
}

/**
 * A site's SOLID raster from its placements (`[cx, cz, r, ...placements]`) — the
 * server's `decodeSite` up to and including its solid loop, cell for cell (the
 * harness pins the parity). `maskCell` is the mask's own raster cell (the file's
 * `cell`), `gridCell` the world grid's.
 */
export function decodeMaskSite(id: number, raw: number[], keys: MaskKey[], maskCell = 1, gridCell = MASK_GRID_CELL_M, keyCache: Array<KeyMask | undefined> = []): MaskSite {
  const cx = raw[0], cz = raw[1], r = raw[2];
  const R = r + MASK_SITE_REACH_M;
  const n = Math.ceil((2 * R) / gridCell);
  const ox = cx - R, oz = cz - R;
  const solid = new Uint8Array(n * n);
  let placements = 0;
  for (let i = 3; i + MASK_STRIDE <= raw.length; i += MASK_STRIDE) {
    const m = keyMaskOf(keys, raw[i], keyCache);
    if (!m) continue;
    placements++;
    const px = cx + raw[i + 1], pz = cz + raw[i + 2], yaw = raw[i + 3], scale = raw[i + 4];
    const c = Math.cos(yaw), s = Math.sin(yaw);
    // the prefab's mask rectangle (natural units, about its pivot) → world corners → the cells to test
    const ex = m.words ? [m.x0 - m.pcx, m.x0 + m.w * maskCell - m.pcx] : [-m.hw, m.hw];
    const ez = m.words ? [m.z0 - m.pcz, m.z0 + m.h * maskCell - m.pcz] : [-m.hd, m.hd];
    let wx0 = Infinity, wx1 = -Infinity, wz0 = Infinity, wz1 = -Infinity;
    for (const qx of ex) for (const qz of ez) {
      const wx = px + (qx * c + qz * s) * scale, wz = pz + (-qx * s + qz * c) * scale;
      wx0 = Math.min(wx0, wx); wx1 = Math.max(wx1, wx); wz0 = Math.min(wz0, wz); wz1 = Math.max(wz1, wz);
    }
    const gx0 = Math.max(0, Math.floor((wx0 - ox) / gridCell)), gx1 = Math.min(n - 1, Math.floor((wx1 - ox) / gridCell));
    const gz0 = Math.max(0, Math.floor((wz0 - oz) / gridCell)), gz1 = Math.min(n - 1, Math.floor((wz1 - oz) / gridCell));
    const inv = 1 / scale;
    for (let gz = gz0; gz <= gz1; gz++) for (let gx = gx0; gx <= gx1; gx++) {
      const idx = gz * n + gx;
      if (solid[idx]) continue;
      const wx = ox + (gx + 0.5) * gridCell, wz = oz + (gz + 0.5) * gridCell;
      const rx = (wx - px) * inv, rz = (wz - pz) * inv;               // world offset, unscaled
      const qx = m.pcx + (rx * c - rz * s), qz = m.pcz + (rx * s + rz * c); // un-rotated → natural prefab coords
      if (maskSolid(m, qx, qz, maskCell)) solid[idx] = 1;
    }
  }
  return { id, cx, cz, r, ox, oz, n, cell: gridCell, solid, placements };
}

// ── Module state: the loaded file, the decoded LRU, the counters ────────────

let wantSeed = '';
let file: MaskFile | null = null;
let fileSeed = '';
let loading = false;
let status = 'idle'; // idle | loading | loaded | absent | invalid | failed
const keyCache: Array<KeyMask | undefined> = [];
const decoded = new Map<number, MaskSite>(); // insertion order = recency (touched entries re-inserted)
let cacheBytes = 0;
interface SiteEntry { id: number; cx: number; cz: number; rr: number; rr2: number; order: number; beats: SiteEntry[] }
let siteList: SiteEntry[] | null = null;
/** id → entry, for the fast path (rebuilt with the list). */
const siteEntryById = new Map<number, SiteEntry>();
let lastSite: MaskSite | null = null;
const stats = { queries: 0, walls: 0, pushOuts: 0, decodes: 0, evictions: 0 };

function siteBytes(site: MaskSite): number { return site.solid.length; }

/** Point the module at a seed: the file is fetched lazily (once per seed); a 404 leaves no walls. */
export function setStructureMasksSeed(seed: string): void {
  if (!seed || seed === wantSeed) return;
  wantSeed = seed;
  if (file && fileSeed !== seed) setStructureMasksFile(null, '');
  if (typeof window === 'undefined' || typeof fetch !== 'function') return; // harness / Node: files arrive by setStructureMasksFile
  if (loading) return; // the running fetch re-arms for the newest seed when it lands
  loading = true; status = 'loading';
  void (async () => {
    try {
      const res = await fetch(`/footprints/${maskFileFor(seed)}`);
      if (!res.ok) { status = 'absent'; return; } // not baked yet: no walls, the bands answer
      const json: unknown = await res.json();
      const problem = maskFileProblem(json, seed);
      if (problem) { console.warn(`[structure-masks] ignoring ${maskFileFor(seed)}: ${problem}`); status = 'invalid'; return; }
      if (wantSeed === seed) setStructureMasksFile(json as MaskFile, seed);
    } catch (e) { status = 'failed'; console.warn('[structure-masks] load failed', (e as Error)?.message ?? e); }
    finally {
      loading = false;
      if (wantSeed && wantSeed !== seed) { const w = wantSeed; wantSeed = ''; setStructureMasksSeed(w); } // the seed moved while we fetched
    }
  })();
}

/** Install a parsed footprints file directly (the fetch path, harnesses, tests); null clears everything. */
export function setStructureMasksFile(f: MaskFile | null, seed = f?.seed ?? ''): void {
  file = f;
  fileSeed = f ? seed : '';
  if (f) { wantSeed = seed; status = 'loaded'; } else if (status === 'loaded') status = 'idle';
  keyCache.length = 0;
  decoded.clear();
  cacheBytes = 0;
  siteList = null;
  lastSite = null;
}

/** The decoded site (lazily, through the byte-bounded LRU). */
function siteById(id: number): MaskSite | null {
  const hit = decoded.get(id);
  if (hit) { decoded.delete(id); decoded.set(id, hit); return hit; } // touch: most recent last
  const raw = file?.sites[id];
  if (!raw || raw.length < 3) return null;
  const site = decodeMaskSite(id, raw, file!.keys, file!.cell, MASK_GRID_CELL_M, keyCache);
  stats.decodes++;
  const need = siteBytes(site);
  // least recently used first, until this one fits (a grid larger than the whole budget is still kept alone)
  for (const k of [...decoded.keys()]) {
    if (cacheBytes + need <= MASK_CACHE_BYTES) break;
    const old = decoded.get(k)!;
    decoded.delete(k); cacheBytes -= siteBytes(old); stats.evictions++;
    if (lastSite === old) lastSite = null;
  }
  decoded.set(id, site); cacheBytes += need;
  return site;
}

/** The site list, each entry with the sites that WIN over it where their grids overlap (smaller reach, or an equal
 *  reach earlier in list order — the server's tie rule), so the lastSite fast path stays exact. */
function buildSiteList(f: MaskFile): SiteEntry[] {
  const list: SiteEntry[] = Object.keys(f.sites).map((k, order) => {
    const raw = f.sites[+k]; const rr = raw[2] + MASK_SITE_REACH_M;
    return { id: +k, cx: raw[0], cz: raw[1], rr, rr2: rr * rr, order, beats: [] };
  });
  for (const a of list) for (const b of list) {
    if (a === b || !(b.rr < a.rr || (b.rr === a.rr && b.order < a.order))) continue;
    if (Math.hypot(a.cx - b.cx, a.cz - b.cz) <= a.rr + b.rr) a.beats.push(b);
  }
  siteEntryById.clear();
  for (const e of list) siteEntryById.set(e.id, e);
  return list;
}

/**
 * The site whose grid (plan radius + reach) covers (x, z), or null — only for the loaded seed's sites.
 * THE SMALLEST COVERING SITE (2026-09-25, the server's `footprintSiteAt` rule): where two grids overlap — a capital's
 * 730 m core reach over the inner edge of every first-ring district — the FIRST site answered, and the core grid holds
 * no district building, so a hovercraft drove through district buildings in that lens (and the lastSite fast path made
 * the answer depend on where you came from).
 */
function siteAt(x: number, z: number): MaskSite | null {
  if (!file) return null;
  if (!siteList) siteList = buildSiteList(file);
  const covers = (s: SiteEntry): boolean => { const dx = x - s.cx, dz = z - s.cz; return dx * dx + dz * dz <= s.rr2; };
  // The hot path: the hull's three probes and every fixed tick land in the same site — valid only while no site
  // that wins over it also covers the point.
  const last = lastSite ? siteEntryById.get(lastSite.id) : undefined;
  if (last && lastSite && covers(last) && !last.beats.some(covers)) return lastSite;
  let best: SiteEntry | null = null;
  for (const s of siteList) if (covers(s) && (!best || s.rr < best.rr)) best = s;
  if (!best) return null;
  const site = siteById(best.id);
  if (site) lastSite = site;
  return site;
}

function cellIdx(site: MaskSite, x: number, z: number): number {
  const gx = Math.floor((x - site.ox) / site.cell), gz = Math.floor((z - site.oz) / site.cell);
  if (gx < 0 || gz < 0 || gx >= site.n || gz >= site.n) return -1;
  return gz * site.n + gx;
}

/** Does a loaded site's grid cover (x, z)? (false: the mask has no opinion here — the bands answer) */
export function maskCovers(x: number, z: number): boolean {
  const site = siteAt(x, z);
  return site !== null && cellIdx(site, x, z) >= 0;
}

/** A wall (or plinth, pillar) at walking height at world (x, z)? Off every grid: no. */
export function wallAt(x: number, z: number): boolean {
  stats.queries++;
  const site = siteAt(x, z);
  if (!site) return false;
  const i = cellIdx(site, x, z);
  const hit = i >= 0 && site.solid[i] === 1;
  if (hit) stats.walls++;
  return hit;
}

/**
 * The nearest FREE cell centre within `maxCells` rings of a wall point (rings
 * outward, the closest cell of the first ring that has one — deterministic), or
 * null when the point is free (or off every grid, or buried deeper than the search
 * reaches — a hull cannot have got there in one step; the caller's next test decides).
 *
 * THE BODY'S SIDE: a 1 m wall is ONE cell, and a probe landing in it is a coin
 * flip between its two faces by distance alone — half the nose hits at speed
 * would have come out on the far side, through the wall. With `(fromX, fromZ)`
 * (the hull's centre) the cells on the body's side of the probe are searched
 * first, the rest only if none is open within reach.
 */
export function wallPushOut(x: number, z: number, maxCells = WALL_PUSH_MAX_CELLS, fromX = x, fromZ = z): { x: number; z: number } | null {
  const site = siteAt(x, z);
  if (!site) return null;
  const gx = Math.floor((x - site.ox) / site.cell), gz = Math.floor((z - site.oz) / site.cell);
  if (gx < 0 || gz < 0 || gx >= site.n || gz >= site.n) return null;
  if (!site.solid[gz * site.n + gx]) return null;
  const n = site.n, cell = site.cell;
  const tx = fromX - x, tz = fromZ - z;
  const biased = tx * tx + tz * tz > 1e-12;
  for (let pass = biased ? 0 : 1; pass < 2; pass++) {
    for (let rr = 1; rr <= maxCells; rr++) {
      let bx = 0, bz = 0, bd = Infinity;
      for (let dz = -rr; dz <= rr; dz++) for (let dx = -rr; dx <= rr; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== rr) continue;
        const cx = gx + dx, cz = gz + dz;
        if (cx < 0 || cz < 0 || cx >= n || cz >= n) continue;
        if (site.solid[cz * n + cx]) continue;
        const wx = site.ox + (cx + 0.5) * cell, wz = site.oz + (cz + 0.5) * cell;
        if (pass === 0 && (wx - x) * tx + (wz - z) * tz < 0) continue; // the far side of the wall: last resort only
        const d = (wx - x) * (wx - x) + (wz - z) * (wz - z);
        if (d < bd) { bd = d; bx = wx; bz = wz; }
      }
      if (bd < Infinity) { stats.pushOuts++; return { x: bx, z: bz }; }
    }
  }
  return null;
}

/**
 * The hull ↔ structure resolver (AtlasVehicleModule.hoverBlocked's body, pure so the
 * harness drives it): the hull is probed at the `probes` lever arms along its
 * heading (nose first), the deepest push-out moves the CENTRE and its lever arm
 * goes back so the integrator can yaw the hull off the corner it caught.
 *
 * ON THE GROUND (agl < WALL_BAND_TOP_M) inside a mask site THE MASK IS THE TRUTH:
 * the hull line is sampled at cell pitch between the probes (a 1 m wall cannot
 * slip between the nose and the centre at 150 m/s), a wall cell pushes to the
 * nearest open one, a free cell is free — the band solids of masked buildings are
 * skipped there, only an UNMASKED solid (no ladder in the proxies) still blocks
 * by its box. Off the ground, or off every grid, the band solids answer.
 */
export function hullBlockedAt(x: number, z: number, y: number, yaw: number, agl: number, probes: readonly number[], margin: number): { x: number; z: number; lever?: number } | null {
  const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
  const low = agl < WALL_BAND_TOP_M;
  let bx = 0, bz = 0, bd = 0, bl = 0, any = false;
  const take = (px: number, pz: number, out: { x: number; z: number }, lever: number): void => {
    const dx = out.x - px, dz = out.z - pz;
    const d = Math.hypot(dx, dz);
    if (!any || d > bd) { any = true; bd = d; bx = dx; bz = dz; bl = lever; }
  };
  for (const lever of probes) {
    const px = x + fx * lever, pz = z + fz * lever;
    if (low && maskCovers(px, pz)) {
      if (wallAt(px, pz)) { const out = wallPushOut(px, pz, WALL_PUSH_MAX_CELLS, x, z); if (out) take(px, pz, out, lever); continue; }
      const hit = structureHitAt(px, pz, y, margin, true);
      if (hit) take(px, pz, structurePushOut(hit, px, pz, margin + 1), lever);
      continue;
    }
    const hit = structureHitAt(px, pz, y, margin);
    if (!hit) continue;
    take(px, pz, structurePushOut(hit, px, pz, margin + 1), lever);
  }
  if (low && probes.length > 1) {
    // Between the probes, at cell pitch: only the mask (the bands were already asked at the probes).
    let lo = Infinity, hi = -Infinity;
    for (const l of probes) { if (l < lo) lo = l; if (l > hi) hi = l; }
    for (let lever = hi - MASK_GRID_CELL_M; lever > lo; lever -= MASK_GRID_CELL_M) {
      const px = x + fx * lever, pz = z + fz * lever;
      if (!wallAt(px, pz)) continue;
      const out = wallPushOut(px, pz, WALL_PUSH_MAX_CELLS, x, z);
      if (out) take(px, pz, out, lever);
    }
  }
  return any ? { x: x + bx, z: z + bz, lever: bl } : null;
}

/** Diag (`__structureMasks()`): what is loaded, what is decoded, and how often the walls answered. */
export function structureMasksDiag(): { seed: string; status: string; file: string; keys: number; sites: number; decoded: number; bytes: number; cacheBytes: number; queries: number; walls: number; pushOuts: number; decodes: number; evictions: number; decodedIds: number[] } {
  return {
    seed: wantSeed, status, file: wantSeed ? maskFileFor(wantSeed) : '',
    keys: file?.keys.length ?? 0, sites: file ? Object.keys(file.sites).length : 0,
    decoded: decoded.size, bytes: cacheBytes, cacheBytes: MASK_CACHE_BYTES,
    ...stats, decodedIds: [...decoded.keys()],
  };
}

/** Test-only: wipe the module (harness fixtures). */
export function __resetStructureMasks(): void {
  setStructureMasksFile(null, '');
  wantSeed = ''; loading = false; status = 'idle';
  stats.queries = stats.walls = stats.pushOuts = stats.decodes = stats.evictions = 0;
}
